/**
 * Responsibility: the one shape every card dialog takes.
 *
 * Three pages open a dialog about a card, and they had drifted into three
 * sizes and three scrolling behaviours. This is the single definition:
 * same width, same height, same tab strip, same actions row. A page
 * supplies a title, its tabs and its buttons, and nothing else about the
 * frame is its business.
 *
 * **The dialog never scrolls.** That is the whole reason it has tabs - if
 * the content spilled, the tabs were not doing their job. The body is a
 * fixed height with hidden overflow, and anything genuinely unbounded
 * inside it (a sale list, a page of listings) scrolls within its own
 * region. `ScrollRegion` is here so those places declare that explicitly
 * rather than letting the dialog grow and hoping.
 *
 * The height is fixed rather than fitted to the tallest tab, so switching
 * tabs does not resize the box under the pointer.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

/**
 * The body's height. Roughly square against the `md` width, which is the
 * shape this settled on and should stay: it holds a card beside its
 * figures with a chart underneath, without scrolling.
 *
 * Capped against the viewport so a short laptop screen still fits the
 * whole dialog, chrome included, rather than scrolling the page behind it.
 */
const BODY_HEIGHT = { xs: '62vh', md: 'min(560px, 66vh)' };

export type DialogTab = {
  label: string;
  content: ReactNode;
};

type CardDialogShellProps = {
  open: boolean;
  title: ReactNode;
  tabs: DialogTab[];
  actions?: ReactNode;
  tab: number;
  onTab: (next: number) => void;
  onClose: () => void;
};

/**
 * A region inside the dialog that is allowed to scroll.
 *
 * Fills the body and scrolls internally, so a hundred sales or forty
 * listings never push the dialog itself out of shape.
 */
export function ScrollRegion({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>
      {children}
    </Box>
  );
}

export function CardDialogShell({
  open,
  title,
  tabs,
  actions,
  tab,
  onTab,
  onClose,
}: CardDialogShellProps) {
  const current = tabs[Math.min(tab, tabs.length - 1)];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{title}</DialogTitle>

      <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={Math.min(tab, tabs.length - 1)}
          onChange={(_event, next: number) => onTab(next)}
          variant="scrollable"
          scrollButtons={false}
        >
          {tabs.map((entry) => (
            <Tab key={entry.label} label={entry.label} />
          ))}
        </Tabs>
      </Box>

      {/* Fixed height, hidden overflow: the dialog itself must never
          scroll, and a tab that needs more room scrolls inside. */}
      <DialogContent sx={{ height: BODY_HEIGHT, overflow: 'hidden', pb: 1 }}>
        <Stack sx={{ height: '100%', minWidth: 0 }}>{current?.content}</Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions>
    </Dialog>
  );
}
